<template>
  <div class="refund-request-modal-wrapper">
    <b-modal
      :visible="visible"
      scrollable
      centered
      size="lg"
      dialog-class="refund-request-dialog"
      header-class="px-3 px-lg-4 pt-4 pb-0 border-0"
      body-class="px-3 px-lg-4 py-3"
      footer-class="px-3 px-lg-4 pt-3 pb-4 border-0"
      :modal-class="`theme-${$setup.theme}-modals`"
      @change="$emit('change', $event)"
    >
      <template #modal-header>
        <div
          class="modal-top d-flex align-items-center justify-content-between w-100"
        >
          <h5 class="mb-0">Solicitar reembolso</h5>
          <button class="btn-close-modal" @click="close">
            <i class="icon-close" />
          </button>
        </div>
      </template>

      <div
        class="refund-request-modal"
        :class="{ 'is-dark': $setup.theme === 'dark' }"
      >
        <div class="product-box mb-3">
          <div class="product-name">{{ productName }}</div>
          <div v-if="amountLabel" class="product-amount">
            Valor da compra: {{ amountLabel }}
          </div>
        </div>

        <div class="deadline-box mb-3">{{ deadlineText }}</div>

        <form-group
          id="refund-request-reason"
          v-model="reason"
          :errors="fieldErrors('reason')"
          textarea
          label="Motivo da solicitação"
          placeholder="Conte pro produtor por que você quer o reembolso (mínimo de 10 caracteres)"
        />
        <div
          class="reason-counter text-right"
          :class="{ 'is-invalid-counter': !reasonLengthValid }"
        >
          {{ reason.length }}/1000
        </div>

        <div v-if="isBillet" class="bank-box mt-3">
          <h6 class="mb-1">Dados bancários para receber o estorno</h6>
          <p class="bank-hint mb-3">
            A conta precisa ser do titular da compra. O estorno do boleto é
            enviado por TED depois da aprovação do produtor.
          </p>

          <div v-if="loadingBanks" class="loading-banks mb-3">
            Carregando bancos...
          </div>
          <div v-else-if="banksError" class="banks-error mb-3">
            Não foi possível carregar a lista de bancos.
            <button type="button" class="btn-link-retry" @click="loadBanks">
              Tentar novamente
            </button>
          </div>

          <div class="form-group">
            <label for="refund-request-bank-search">Banco</label>
            <input
              id="refund-request-bank-search"
              v-model="bankSearch"
              class="form-control mb-2"
              type="text"
              placeholder="Buscar pelo nome ou código do banco"
              autocomplete="off"
            />
            <select
              id="refund-request-bank"
              v-model="bankAccount.bank"
              class="form-control"
            >
              <option value="">Selecione o banco</option>
              <option
                v-for="bank in bankOptions"
                :key="bank.compe"
                :value="bank.compe"
              >
                {{ bank.compe }} - {{ bankLabel(bank) }}
              </option>
            </select>
            <div v-if="fieldMessage('bank')" class="field-error">
              {{ fieldMessage("bank") }}
            </div>
          </div>

          <div class="row">
            <div class="col-8">
              <form-group
                id="refund-request-branch-number"
                v-model="bankAccount.branch_number"
                :errors="fieldErrors('branch_number')"
                label="Agência (sem dígito)"
                placeholder="0000"
              />
            </div>
            <div class="col-4">
              <form-group
                id="refund-request-branch-check-digit"
                v-model="bankAccount.branch_check_digit"
                :errors="fieldErrors('branch_check_digit')"
                label="Dígito (opcional)"
                placeholder="0"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-8">
              <form-group
                id="refund-request-account-number"
                v-model="bankAccount.account_number"
                :errors="fieldErrors('account_number')"
                label="Conta (sem dígito)"
                placeholder="00000"
              />
            </div>
            <div class="col-4">
              <form-group
                id="refund-request-account-check-digit"
                v-model="bankAccount.account_check_digit"
                :errors="fieldErrors('account_check_digit')"
                label="Dígito"
                placeholder="0"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="refund-request-account-type">Tipo de conta</label>
            <select
              id="refund-request-account-type"
              v-model="bankAccount.type"
              class="form-control"
            >
              <option value="checking">Conta corrente</option>
              <option value="savings">Conta poupança</option>
            </select>
            <div v-if="fieldMessage('type')" class="field-error">
              {{ fieldMessage("type") }}
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div
          class="modal-actions d-flex align-items-center justify-content-end w-100 m-0"
        >
          <button class="btn btn-outline-secondary mr-2" @click="close">
            Cancelar
          </button>
          <button
            class="btn btn-primary px-4"
            :disabled="loading"
            @click="askConfirmation"
          >
            <loading :show="loading" small>Enviar solicitação</loading>
          </button>
        </div>
      </template>
    </b-modal>

    <confirmation
      v-model="confirming"
      title="Enviar solicitação de reembolso?"
      :text="confirmationText"
      confirm-button-text="Enviar"
      @confirm="submit"
    />
  </div>
</template>

<script>
const ACCOUNT_NUMBER_MAX_DIGITS = 13;

export default {
  name: "RefundRequestModal",
  model: {
    prop: "visible",
    event: "change"
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      reason: "",
      bankAccount: this.emptyBankAccount(),
      bankSearch: "",
      banks: [],
      loadingBanks: false,
      banksError: false,
      loading: false,
      confirming: false,
      errors: {}
    };
  },
  computed: {
    productName() {
      return (this.item && this.item.product_name) || "";
    },
    paymentProcess() {
      return (this.item && this.item.payment_process) || "";
    },
    isBillet() {
      return this.paymentProcess === "billet";
    },
    amountLabel() {
      const amount = this.item && this.item.amount;
      if (amount === null || amount === undefined || amount === "") {
        return "";
      }
      const value = Number(amount);
      if (isNaN(value)) {
        return "";
      }
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    },
    deadlineText() {
      switch (this.paymentProcess) {
        case "card":
        case "two_card":
        case "smart_card":
          return "Depois da aprovação do produtor, o estorno é lançado no cartão e pode levar até 2 faturas para aparecer, conforme o prazo do banco emissor.";
        case "pix":
          return "Depois da aprovação do produtor, o estorno do Pix cai na conta de origem em alguns segundos.";
        case "billet":
          return "Depois da aprovação do produtor, o estorno do boleto é enviado por TED em 1 a 2 dias úteis. Por isso precisamos dos seus dados bancários.";
        case "card_pix":
          return "Depois da aprovação do produtor, a parte paga no cartão pode levar até 2 faturas e a parte paga no Pix cai na conta de origem em alguns segundos.";
        default:
          return "Depois da aprovação do produtor, o prazo do estorno depende da forma de pagamento usada na compra.";
      }
    },
    reasonLengthValid() {
      const length = this.reason.trim().length;
      return length >= 10 && length <= 1000;
    },
    bankOptions() {
      const search = this.bankSearch.trim().toLowerCase();
      if (!search) {
        return this.banks;
      }
      return this.banks.filter(bank => {
        const haystack = [bank.compe, bank.long_name, bank.short_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      });
    },
    confirmationText() {
      return `Vamos enviar sua solicitação de reembolso de "${this.productName}" para o produtor analisar. Você será avisado por e-mail sobre a decisão.`;
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetForm();
        if (this.isBillet && !this.banks.length) {
          this.loadBanks();
        }
      }
    },
    reason(value) {
      if (value && value.length > 1000) {
        this.reason = value.slice(0, 1000);
      }
    },
    "bankAccount.branch_number"(value) {
      const clean = this.onlyDigits(value, 6);
      if (clean !== value) {
        this.bankAccount.branch_number = clean;
      }
    },
    "bankAccount.account_number"(value) {
      const clean = this.onlyDigits(value, ACCOUNT_NUMBER_MAX_DIGITS);
      if (clean !== value) {
        this.bankAccount.account_number = clean;
      }
    },
    "bankAccount.branch_check_digit"(value) {
      const clean = this.checkDigit(value);
      if (clean !== value) {
        this.bankAccount.branch_check_digit = clean;
      }
    },
    "bankAccount.account_check_digit"(value) {
      const clean = this.checkDigit(value);
      if (clean !== value) {
        this.bankAccount.account_check_digit = clean;
      }
    }
  },
  methods: {
    emptyBankAccount() {
      return {
        bank: "",
        branch_number: "",
        branch_check_digit: "",
        account_number: "",
        account_check_digit: "",
        type: "checking"
      };
    },
    onlyDigits(value, maxLength) {
      return String(value || "")
        .replace(/\D/g, "")
        .slice(0, maxLength);
    },
    checkDigit(value) {
      return String(value || "")
        .replace(/[^0-9xX]/g, "")
        .toUpperCase()
        .slice(0, 2);
    },
    bankLabel(bank) {
      return bank.long_name || bank.short_name || "";
    },
    resetForm() {
      this.reason = "";
      this.bankAccount = this.emptyBankAccount();
      this.bankSearch = "";
      this.errors = {};
      this.loading = false;
      this.confirming = false;
    },
    close() {
      this.$emit("change", false);
    },
    loadBanks() {
      this.loadingBanks = true;
      this.banksError = false;
      this.$store
        .dispatch("refundRequests/listBanks")
        .then(banks => {
          this.banks = banks;
        })
        .catch(() => {
          this.banksError = true;
        })
        .finally(() => {
          this.loadingBanks = false;
        });
    },
    fieldErrors(field) {
      return this.errors[field] || [];
    },
    fieldMessage(field) {
      const messages = this.errors[field];
      return messages && messages.length ? messages[0] : "";
    },
    validate() {
      const errors = {};
      const reason = this.reason.trim();
      if (reason.length < 10) {
        errors.reason = ["Escreva o motivo com pelo menos 10 caracteres."];
      } else if (reason.length > 1000) {
        errors.reason = ["O motivo pode ter no máximo 1000 caracteres."];
      }
      if (this.isBillet) {
        if (!this.bankAccount.bank) {
          errors.bank = ["Selecione o banco."];
        }
        if (!this.bankAccount.branch_number) {
          errors.branch_number = ["Informe a agência."];
        }
        if (!this.bankAccount.account_number) {
          errors.account_number = ["Informe a conta."];
        }
        if (!this.bankAccount.account_check_digit) {
          errors.account_check_digit = ["Informe o dígito da conta."];
        }
      }
      this.errors = errors;
      return !Object.keys(errors).length;
    },
    askConfirmation() {
      if (this.loading) {
        return;
      }
      if (!this.validate()) {
        this.$message.error("Revise os campos destacados para continuar.");
        return;
      }
      this.confirming = true;
    },
    buildPayload() {
      const payload = {
        product_id: this.item.product_id,
        reason: this.reason.trim()
      };
      if (this.isBillet) {
        payload.bank_account = {
          bank: this.bankAccount.bank,
          branch_number: this.bankAccount.branch_number,
          account_number: this.bankAccount.account_number,
          account_check_digit: this.bankAccount.account_check_digit,
          type: this.bankAccount.type
        };
        if (this.bankAccount.branch_check_digit) {
          payload.bank_account.branch_check_digit = this.bankAccount.branch_check_digit;
        }
      }
      return payload;
    },
    submit() {
      if (this.loading || !this.item) {
        return;
      }
      this.loading = true;
      this.$store
        .dispatch("refundRequests/create", this.buildPayload())
        .then(data => {
          this.$emit("created", data);
          this.close();
        })
        .catch(error => {
          this.handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleError(error) {
      const response = (error && error.response) || {};
      const data = response.data;

      if (response.status === 400 && data && typeof data === "object") {
        this.errors = this.parseFieldErrors(data);
        this.$message.error(
          this.firstFieldMessage() || "Revise os campos destacados."
        );
        return;
      }

      if (data && data.message) {
        this.$message.error(data.message);
        return;
      }

      if (response.status === 429) {
        this.$message.error(
          "Você fez muitas solicitações seguidas. Tente novamente mais tarde."
        );
        return;
      }

      this.$message.error(
        "Não foi possível enviar a solicitação agora. Tente novamente em instantes."
      );
    },
    parseFieldErrors(data) {
      const errors = {};
      Object.keys(data).forEach(key => {
        const value = data[key];
        if (Array.isArray(value)) {
          errors[key] = value.map(String);
        } else if (value && typeof value === "object") {
          Object.keys(value).forEach(inner => {
            const innerValue = value[inner];
            errors[inner] = Array.isArray(innerValue)
              ? innerValue.map(String)
              : [String(innerValue)];
          });
        } else if (value) {
          errors[key] = [String(value)];
        }
      });
      return errors;
    },
    firstFieldMessage() {
      const keys = Object.keys(this.errors);
      for (let i = 0; i < keys.length; i++) {
        const messages = this.errors[keys[i]];
        if (messages && messages.length) {
          return messages[0];
        }
      }
      return "";
    }
  }
};
</script>

<style lang="scss">
@media screen and (min-width: 576px) {
  .refund-request-dialog.modal-dialog-scrollable {
    margin-top: 88px;
    margin-bottom: 16px;
    min-height: calc(100% - 104px);
    max-height: calc(100% - 104px);
  }
}
</style>

<style lang="scss" scoped>
.modal-top {
  h5 {
    font-weight: 600;
  }

  .btn-close-modal {
    border: none;
    background: transparent;
    color: inherit;
    padding: 0;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }

    i {
      font-size: 16px;
    }
  }
}

.refund-request-modal {
  .product-box {
    .product-name {
      font-weight: 500;
      font-size: 15px;
    }

    .product-amount {
      font-size: 13px;
      opacity: 0.75;
    }
  }

  .deadline-box {
    border-radius: 8px;
    padding: 12px;
    font-size: 13px;
    line-height: 150%;
    background: rgba(105, 106, 126, 0.08);
  }

  .reason-counter {
    font-size: 12px;
    opacity: 0.7;
    margin-top: -8px;

    &.is-invalid-counter {
      color: #ff757e;
      opacity: 1;
    }
  }

  .bank-box {
    border-top: 1px solid rgba(105, 106, 126, 0.3);
    padding-top: 16px;

    h6 {
      font-size: 14px;
      font-weight: 600;
    }

    .bank-hint {
      font-size: 12px;
      opacity: 0.75;
    }

    .loading-banks,
    .banks-error {
      font-size: 12px;
      opacity: 0.75;
    }

    .btn-link-retry {
      border: none;
      background: transparent;
      padding: 0;
      text-decoration: underline;
      color: inherit;
    }
  }

  .field-error {
    font-size: 12px;
    color: #ff757e;
    margin-top: 4px;
  }

  select.form-control {
    min-height: 48px;
    border-radius: 4px;
    font-size: 14px;

    option {
      color: #3b3b4a;
      background: #fff;
    }
  }

  &.is-dark {
    .form-control {
      border: 1px solid var(--verso-dark-grey-1, #bebfc7);
      background-color: transparent;
      color: #fff;

      &::placeholder {
        color: var(--verso-dark-grey-2, #6b6e73);
      }
    }
  }
}
</style>
